import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from 'react-query';
import { AxiosError } from 'axios';
import {
  cancelBulkTransactionService,
  cancelTransactionService,
  changePinService,
  checkIfPinExistsService,
  createBulkTransactionService,
  createPaymentLinkTransactionService,
  createPinService,
  createTransactionService,
  getBulkTransactionItemsService,
  getBulkTransactionsService,
  getPaymentLinkTransactionsService,
  getReceiverTransactionAmountsSummaryService,
  getReceiverTransactionsService,
  getReceiverTransactionSummaryService,
  getSenderTransactionSummaryService,
  getSettlementsAccountTransactionsService,
  getSingleTransactionByReferenceService,
  getSingleTransactionsService,
  getTransactionsByTxrefService,
  loadPaymentCodeService,
  loadVoucherService,
  nethoneCheckByTxrefPublicService,
  nethoneCheckByTxrefService,
  resetPinService
} from '@src/services/transactions-service';
import Api from '@sendsprint/api-types';
import ClientApi from '@src/types/client';
import { CONFIG_NO_ERROR_TOAST, DURATION_1_HR } from '@src/constants';
import { useAccount } from '@src/contexts';

export const useCreateTransactionMutation = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.General.TransactionRefInfo,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Transactions.CreateTransaction.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.General.TransactionRefInfo,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Transactions.CreateTransaction.Request
  >('createTransaction', createTransactionService, options);
};

export const useCreateBulkTransactionMutation = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.Transactions.CreateBulkTransaction.Response['data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Transactions.CreateBulkTransaction.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.Transactions.CreateBulkTransaction.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Transactions.CreateBulkTransaction.Request
  >('createBulkTransaction', createBulkTransactionService, options);
};

export const useCreatePaymentLinkTransactionMutation = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.Transactions.CreatePaymentLinkTransaction.Response['data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Transactions.CreatePaymentLinkTransaction.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.Transactions.CreatePaymentLinkTransaction.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Transactions.CreatePaymentLinkTransaction.Request
  >('createPaymentLinkTransaction', createPaymentLinkTransactionService, options);
};

export const useCancelTransaction = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.Transactions.CancelTransaction.Response['Data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Transactions.CancelTransaction.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.Transactions.CancelTransaction.Response['Data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Transactions.CancelTransaction.Request
  >('cancelTransaction', cancelTransactionService, options);
};

export const useCancelBulkTransaction = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.Transactions.CancelBulkTransaction.Response['data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Transactions.CancelBulkTransaction.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.Transactions.CancelBulkTransaction.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Transactions.CancelBulkTransaction.Request
  >('cancelBulkTransaction', cancelBulkTransactionService, options);
};

export const useGetTransactionByTxref = (
  payload: ClientApi.Transactions.GetTransactionByTxref.Request,
  options?: UseQueryOptions<
    null | ClientApi.General.Transaction,
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    null | ClientApi.General.Transaction,
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: ['getTransactionByTxref', payload.txref, isLoggedIn],
    queryFn: () => getTransactionsByTxrefService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    ...options
  });
};

export const useCheckIfPinExists = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.Transactions.CheckIfPinExists.Response,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.Transactions.CheckIfPinExists.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  >('checkIfPinExists', checkIfPinExistsService, options);
};

export const useCheckIfPinExistsQuery = (
  options?: UseQueryOptions<
    ClientApi.Transactions.CheckIfPinExists.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    ClientApi.Transactions.CheckIfPinExists.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: ['checkIfPinExistsQuery', isLoggedIn],
    queryFn: () => checkIfPinExistsService(CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    ...options
  });
};

export const useCreatePin = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.Transactions.CreatePin.Response,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Transactions.CreatePin.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.Transactions.CreatePin.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Transactions.CreatePin.Request
  >('createPin', createPinService, options);
};

export const useChangePin = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.Transactions.ChangePin.Response['Data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Transactions.ChangePin.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.Transactions.ChangePin.Response['Data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Transactions.ChangePin.Request
  >('changePin', changePinService, options);
};

export const useResetPin = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.Transactions.ResetPin.Response['Data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Transactions.ResetPin.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.Transactions.ResetPin.Response['Data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Transactions.ResetPin.Request
  >('resetPin', resetPinService, options);
};

export const useGetBulkTransactions = (
  payload: ClientApi.Transactions.GetBulkTransactions.Request,
  options?: UseQueryOptions<
    null | ClientApi.Transactions.GetBulkTransactions.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    null | ClientApi.Transactions.GetBulkTransactions.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: [
      'getBulkTransactions',
      isLoggedIn,
      payload.endDate,
      payload.pageNumber,
      payload.pageSize,
      payload.periodSelector,
      payload.startDate,
      payload.searchValue
    ],
    queryFn: () => getBulkTransactionsService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    keepPreviousData: true,
    ...options
  });
};

export const useGetBulkTransactionItems = (
  payload: ClientApi.Transactions.GetBulkTransactionItems.Request,
  options?: UseQueryOptions<
    null | ClientApi.Transactions.GetBulkTransactionItems.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    null | ClientApi.Transactions.GetBulkTransactionItems.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: ['getBulkTransactionItems', isLoggedIn, payload.transactionReference],
    queryFn: () => getBulkTransactionItemsService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    keepPreviousData: true,
    ...options
  });
};

export const useGetSingleTransactions = (
  payload: ClientApi.Transactions.GetSingleTransactions.Request,
  options?: UseQueryOptions<
    null | ClientApi.Transactions.GetSingleTransactions.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    null | ClientApi.Transactions.GetSingleTransactions.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: [
      'getSingleTransactions',
      isLoggedIn,
      payload.endDate,
      payload.pageNumber,
      payload.pageSize,
      payload.periodSelector,
      payload.startDate,
      payload.searchValue,
      payload.transactionType
    ],
    queryFn: () => getSingleTransactionsService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    keepPreviousData: true,
    ...options
  });
};

export const useGetSingleTransactionByReference = (
  payload: ClientApi.Transactions.GetSingleTransactionByReference.Request,
  options?: UseQueryOptions<
    null | ClientApi.Transactions.GetSingleTransactionByReference.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    null | ClientApi.Transactions.GetSingleTransactionByReference.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: [
      'getSingleTransactionByReference',
      isLoggedIn,
      payload.transactionReference,
      payload.isRecipientUser
    ],
    queryFn: () => getSingleTransactionByReferenceService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    keepPreviousData: true,
    ...options
  });
};

export const useLoadVoucher = (
  options?: Omit<
    UseMutationOptions<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Transactions.LoadVoucher.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Transactions.LoadVoucher.Request
  >('loadVoucher', loadVoucherService, options);
};

export const useLoadPaymentCode = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.Transactions.LoadPaymentCode.PaymentCodeI[],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Transactions.LoadPaymentCode.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.Transactions.LoadPaymentCode.PaymentCodeI[],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Transactions.LoadPaymentCode.Request
  >('loadPaymentCode', loadPaymentCodeService, options);
};

export const useNethoneCheckMutation = (
  options?: Omit<
    UseMutationOptions<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Transactions.NethoneCheckByTxref.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Transactions.NethoneCheckByTxref.Request
  >('nethoneCheckMutation', nethoneCheckByTxrefService, options);
};

export const useNethoneCheckPublicMutation = (
  options?: Omit<
    UseMutationOptions<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.Transactions.NethoneCheckByTxref.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.Transactions.NethoneCheckByTxref.Request
  >('nethoneCheckPublicMutation', nethoneCheckByTxrefPublicService, options);
};

export const useGetSenderTransactionSummary = (
  options?: UseQueryOptions<
    ClientApi.Transactions.GetSenderTransactionSummary.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    ClientApi.Transactions.GetSenderTransactionSummary.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: ['getSenderTransactionSummary', isLoggedIn],
    queryFn: () => getSenderTransactionSummaryService(CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    ...options
  });
};

export const useGetPaymentLinkTransactions = (
  payload: ClientApi.Transactions.GetPaymentLinkTransactions.Request,
  options?: UseQueryOptions<
    ClientApi.Transactions.GetPaymentLinkTransactions.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    ClientApi.Transactions.GetPaymentLinkTransactions.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: [
      'getPaymentLinkTransactions',
      isLoggedIn,
      payload.linkReference,
      payload.endDate,
      payload.pageNumber,
      payload.pageSize,
      payload.periodSelector,
      payload.searchValue,
      payload.startDate
    ],
    queryFn: () => getPaymentLinkTransactionsService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    ...options
  });
};

export const useGetReceiverTransactions = (
  payload: ClientApi.Transactions.GetReceiverTransactions.Request,
  options?: UseQueryOptions<
    ClientApi.Transactions.GetReceiverTransactions.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    ClientApi.Transactions.GetReceiverTransactions.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: [
      'getReceiverTransactions',
      isLoggedIn,
      payload.endDate,
      payload.pageNumber,
      payload.pageSize,
      payload.periodSelector,
      payload.searchValue,
      payload.startDate,
      payload.transactionType,
      payload.isSettled
    ],
    queryFn: () => getReceiverTransactionsService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    keepPreviousData: true,
    ...options
  });
};

export const useGetSettlementAccountTransactions = (
  payload: ClientApi.Transactions.GetSettlementAccountTransactions.Request,
  options?: UseQueryOptions<
    ClientApi.Transactions.GetSettlementAccountTransactions.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    ClientApi.Transactions.GetSettlementAccountTransactions.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: [
      'getSettlementAccountTransactions',
      isLoggedIn,
      payload.endDate,
      payload.pageNumber,
      payload.pageSize,
      payload.periodSelector,
      payload.searchValue,
      payload.startDate,
      payload.accountNumber
    ],
    queryFn: () => getSettlementsAccountTransactionsService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    keepPreviousData: true,
    ...options
  });
};

export const useGetReceiverTransactionSummary = (
  options?: UseQueryOptions<
    ClientApi.Transactions.GetReceiverTransactionSummary.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    ClientApi.Transactions.GetReceiverTransactionSummary.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: ['getReceiverTransactionSummary', isLoggedIn],
    queryFn: () => getReceiverTransactionSummaryService(CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    keepPreviousData: true,
    ...options
  });
};

export const useGetReceiverTransactionAmountsSummary = (
  options?: UseQueryOptions<
    ClientApi.Transactions.GetReceiverTransactionAmountsSummary.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    ClientApi.Transactions.GetReceiverTransactionAmountsSummary.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: ['getReceiverTransactionAmountsSummary', isLoggedIn],
    queryFn: () => getReceiverTransactionAmountsSummaryService(CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    keepPreviousData: true,
    ...options
  });
};
